import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ChevronDown, Search, Trash2, Upload, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod/v4";
import { AICoachPopup } from "@/components/telemetry/ai-report";
import { ChartContainer } from "@/components/telemetry/chart-container";
import { PremiumGate } from "@/components/telemetry/premium-gate";
import { SessionSummary } from "@/components/telemetry/session-summary";
import { TrackMapCursor } from "@/components/telemetry/track-map-cursor";
import { useProfile } from "@/hooks/use-profile";
import { useSubscription } from "@/hooks/use-subscription";
import {
  useCreateAnalysisWithSetup,
  useCreateTelemetryUploadUrl,
  useDeleteUserReference,
  useLoadCachedReference,
  useLoadCachedUserFile,
  useLoadUserReference,
  useSetupsWithTelemetry,
  useUploadAndParse,
  useUploadAndParseCustomReference,
  useUserReferences,
  useUserTelemetryFiles,
} from "@/hooks/use-telemetry";
import { buildCoachingInput } from "@/lib/telemetry-analysis";
import type { ParsedLapData, TurnMarker } from "@/lib/telemetry-types";
export const Route = createFileRoute("/_auth/app/telemetry/")({
  validateSearch: z.object({ setupId: z.string().optional() }),
  component: TelemetryPage,
});

function TelemetryPage() {
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: subscription, isLoading: subLoading } = useSubscription(profile?.id);

  // State
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [storagePath, setStoragePath] = useState<string | null>(null);
  const [userLapData, setUserLapData] = useState<ParsedLapData | null>(null);
  const [refLapData, setRefLapData] = useState<ParsedLapData | null>(null);
  const [turnMarkers, setTurnMarkers] = useState<TurnMarker[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedSetupId, setSelectedSetupId] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [setupSearch, setSetupSearch] = useState("");
  const [detectedInfo, setDetectedInfo] = useState<{ track: string; car: string } | null>(null);
  const [showSetupDropdown, setShowSetupDropdown] = useState(false);
  const [refLoadTrigger, setRefLoadTrigger] = useState(0);
  const [userWeather, setUserWeather] = useState<Record<string, any> | null>(null);
  const [refWeather, setRefWeather] = useState<Record<string, any> | null>(null);
  const [fakeProgress, setFakeProgress] = useState(0);
  const [funMessage, setFunMessage] = useState("");
  const [showProgress, setShowProgress] = useState(false);
  const [_refSource, setRefSource] = useState<"setup" | "custom" | null>(null);
  const [customRefId, setCustomRefId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [cursorDistPct, setCursorDistPct] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const refFileInputRef = useRef<HTMLInputElement>(null);
  const setupDropdownRef = useRef<HTMLDivElement>(null);

  // Pre-select setup from URL search param (e.g. coming from Setup Details page)
  const { setupId: preselectedSetupId } = Route.useSearch();
  const preselectedAppliedRef = useRef(false);

  const uploadMessages = [
    "Warming up the tires...",
    "Rolling out of the pit box...",
    "Checking tire pressures...",
    "Strapping in the harness...",
    "Radio check, radio check...",
    "Waiting for green flag...",
  ];

  const extractMessages = [
    "Analyzing braking zones...",
    "Reading the racing line...",
    "Studying corner entries...",
    "Crunching lap data...",
    "Comparing apex speeds...",
    "Checking throttle traces...",
    "Measuring trail braking...",
    "Reviewing gear changes...",
  ];

  // Fake progress that slowly creeps forward
  useEffect(() => {
    if (!status) {
      // When status clears, flash to 100% briefly before hiding
      if (showProgress) {
        setFakeProgress(100);
        const timeout = setTimeout(() => {
          setShowProgress(false);
          setFakeProgress(0);
        }, 400);
        return () => clearTimeout(timeout);
      }
      return;
    }

    setShowProgress(true);

    // Jump to a base when stage changes
    const isUploading = status === "Uploading..." || status === "Uploading reference...";
    const isExtracting =
      status === "Extracting..." || status === "Parsing..." || status === "Parsing reference...";
    const base = isUploading ? 5 : isExtracting ? 55 : 95;
    setFakeProgress((prev) => Math.max(prev, base));

    // Pick a random fun message
    const messages = isUploading ? uploadMessages : extractMessages;
    setFunMessage(messages[Math.floor(Math.random() * messages.length)]!);

    // Rotate messages
    const msgInterval = setInterval(() => {
      setFunMessage(messages[Math.floor(Math.random() * messages.length)]!);
    }, 2500);

    // Slowly increment progress
    const progressInterval = setInterval(() => {
      setFakeProgress((prev) => {
        const ceiling = isUploading ? 50 : isExtracting ? 90 : 100;
        if (prev >= ceiling) return prev;
        const remaining = ceiling - prev;
        const increment = Math.max(0.3, remaining * 0.04);
        return Math.min(prev + increment, ceiling);
      });
    }, 150);

    return () => {
      clearInterval(msgInterval);
      clearInterval(progressInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Close setup dropdown on outside click
  useEffect(() => {
    if (!showSetupDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (setupDropdownRef.current && !setupDropdownRef.current.contains(e.target as Node)) {
        setShowSetupDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showSetupDropdown]);

  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const fileDropdownRef = useRef<HTMLDivElement>(null);

  // Close file dropdown on outside click
  useEffect(() => {
    if (!showFileDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (fileDropdownRef.current && !fileDropdownRef.current.contains(e.target as Node)) {
        setShowFileDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showFileDropdown]);

  // Hooks
  const { data: setups, isLoading: setupsLoading } = useSetupsWithTelemetry();
  const { data: userFiles } = useUserTelemetryFiles();
  const { data: userReferences } = useUserReferences();

  // Persist + restore selection across navigation (sessionStorage)
  const PERSIST_KEY = "telemetry-selection-v1";
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    if (!userFiles || !setups || !userReferences) return; // wait until lookup data is loaded
    restoredRef.current = true;
    if (typeof window === "undefined") return;
    try {
      const raw = sessionStorage.getItem(PERSIST_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as {
        storagePath?: string | null;
        setupId?: string | null;
        customRefId?: string | null;
      };
      // Restore reference selection (validate it still exists)
      if (saved.setupId && setups.some((s) => s.id === saved.setupId)) {
        setSelectedSetupId(saved.setupId);
        setRefLoadTrigger((n) => n + 1);
      } else if (saved.customRefId && userReferences.some((r) => r.id === saved.customRefId)) {
        setCustomRefId(saved.customRefId);
        setRefSource("custom");
        setRefLoadTrigger((n) => n + 1);
      }
      // Restore user telemetry file (validate it still exists)
      if (saved.storagePath) {
        const match = userFiles.find((f) => f.storagePath === saved.storagePath);
        if (match) {
          handleSelectPastFile(match);
        }
      }
    } catch {
      // bad/old payload — ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only restore once when lookup data first arrives
  }, [userFiles, setups, userReferences]);

  // Save selection whenever it changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!restoredRef.current) return; // don't overwrite saved state with initial empty values
    try {
      sessionStorage.setItem(
        PERSIST_KEY,
        JSON.stringify({
          storagePath: storagePath ?? null,
          setupId: selectedSetupId || null,
          customRefId: customRefId ?? null,
        }),
      );
    } catch {
      // storage full / disabled — silent
    }
  }, [storagePath, selectedSetupId, customRefId]);

  const createUploadUrl = useCreateTelemetryUploadUrl();
  const uploadAndParse = useUploadAndParse();
  const loadCachedUser = useLoadCachedUserFile();
  const createAnalysis = useCreateAnalysisWithSetup();
  const loadReference = useLoadCachedReference();
  const uploadCustomRef = useUploadAndParseCustomReference();
  const loadUserRef = useLoadUserReference();
  const deleteUserRef = useDeleteUserReference();

  // Searchable + auto-filtered setups
  const filteredSetups = useMemo(() => {
    if (!setups) return [];
    let filtered = setups;
    if (detectedInfo) {
      const trackLower = detectedInfo.track.toLowerCase();
      const autoFiltered = filtered.filter(
        (s) =>
          s.trackName.toLowerCase().includes(trackLower) ||
          trackLower.includes(s.trackName.toLowerCase()),
      );
      if (autoFiltered.length > 0) filtered = autoFiltered;
    }
    if (setupSearch) {
      const search = setupSearch.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.carName.toLowerCase().includes(search) ||
          s.trackName.toLowerCase().includes(search) ||
          s.driverName.toLowerCase().includes(search),
      );
    }
    return filtered;
  }, [setups, setupSearch, detectedInfo]);

  const isProcessing =
    createUploadUrl.isPending ||
    uploadAndParse.isPending ||
    loadReference.isPending ||
    uploadCustomRef.isPending ||
    loadUserRef.isPending;

  /** Auto-upload when file is selected */
  const handleFileSelect = useCallback(
    (selectedFile: File | undefined) => {
      if (!selectedFile) return;
      if (!selectedFile.name.toLowerCase().endsWith(".ibt")) {
        setError("Only .ibt telemetry files are accepted");
        return;
      }
      if (selectedFile.size > 150 * 1024 * 1024) {
        setError("File exceeds maximum size of 150 MB");
        return;
      }
      setFile(selectedFile);
      setSelectedFileName(selectedFile.name);
      setError(null);
      setUserLapData(null);
      // Don't clear refLapData — keep the reference visible while user file uploads
      setTurnMarkers([]);

      setStoragePath(null);
      setAnalysisId(null);
      setDetectedInfo(null);
      setUserWeather(null);

      // Auto-upload immediately
      (async () => {
        setStatus("Uploading...");
        try {
          // Step 1: Get signed upload URL from server
          const urlResult = await createUploadUrl.mutateAsync({
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
          });
          if (!urlResult.success) {
            setError(urlResult.error);
            setStatus("");
            return;
          }

          // Step 2: Upload file directly to Supabase (bypasses Vercel 4.5MB limit)
          const uploadRes = await fetch(urlResult.signedUrl, {
            method: "PUT",
            headers: { "Content-Type": "application/octet-stream" },
            body: selectedFile,
          });
          if (!uploadRes.ok) {
            setError("Failed to upload file to storage");
            setStatus("");
            return;
          }

          // Step 3: Parse the uploaded file
          setStatus("Parsing...");
          const result = await uploadAndParse.mutateAsync({
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            storagePath: urlResult.tempPath,
          });

          if (!result.success) {
            setError(result.error);
            setStatus("");
            return;
          }

          setStoragePath(result.storagePath);
          setDetectedInfo({
            track: result.sessionInfo.track_name,
            car: result.sessionInfo.car_name,
          });
          setUserWeather(result.weather);

          // Use best lap data directly from upload response (no second Lambda call)
          if (result.bestLap) {
            setUserLapData(result.bestLap);
            setTurnMarkers(result.turnMarkers);
          }

          // Auto-select matching setup (only if no reference already selected).
          // Prefer setups that match BOTH track and car; fall back to track-only.
          if (!selectedSetupId && !customRefId && result.sessionInfo.track_name && setups) {
            const trackLower = result.sessionInfo.track_name.toLowerCase();
            const carLower = result.sessionInfo.car_name?.toLowerCase() ?? "";
            const trackMatches = setups.filter(
              (s) =>
                s.trackName.toLowerCase().includes(trackLower) ||
                trackLower.includes(s.trackName.toLowerCase()),
            );
            const carAndTrack = carLower
              ? trackMatches.filter(
                  (s) =>
                    s.carName.toLowerCase().includes(carLower) ||
                    carLower.includes(s.carName.toLowerCase()),
                )
              : [];
            const best = carAndTrack[0] ?? (trackMatches.length === 1 ? trackMatches[0] : null);
            if (best) {
              setSelectedSetupId(best.id);
              setSetupSearch(`${best.carName} @ ${best.trackName}`);
            }
          }

          // Reload reference to create analysis record with the new storagePath
          if (selectedSetupId || customRefId) {
            setRefLoadTrigger((n) => n + 1);
          }

          setStatus("");
        } catch (err) {
          setError(err instanceof Error ? err.message : "An unexpected error occurred");
          setStatus("");
        }
      })();
    },
    [createUploadUrl, uploadAndParse, setups],
  );

  /** Select a previously uploaded telemetry file */
  const handleSelectPastFile = useCallback(
    (pastFile: {
      storagePath: string;
      parsedDataUrl: string | null;
      fileName: string;
      laps: any[];
    }) => {
      setShowFileDropdown(false);
      setFile(null);
      setError(null);
      setUserLapData(null);
      // Don't clear refLapData — keep the reference visible while user file loads
      setTurnMarkers([]);

      setAnalysisId(null);
      setDetectedInfo(null);
      setUserWeather(null);
      setStoragePath(pastFile.storagePath);
      setSelectedFileName(pastFile.fileName);

      (async () => {
        // Try cached .parsed.json first (instant — no Lambda)
        if (pastFile.parsedDataUrl) {
          try {
            const cached = await loadCachedUser.mutateAsync({
              parsedDataUrl: pastFile.parsedDataUrl,
            });
            if (cached.success) {
              setUserLapData(cached.lap);
              setTurnMarkers(cached.turnMarkers);
              setDetectedInfo({
                track: cached.sessionInfo.track_name,
                car: cached.sessionInfo.car_name,
              });
              setUserWeather(cached.weather);

              // Auto-match setup (only if no reference already selected)
              if (!selectedSetupId && !customRefId && cached.sessionInfo.track_name && setups) {
                const trackLower = cached.sessionInfo.track_name.toLowerCase();
                const matches = setups.filter(
                  (s) =>
                    s.trackName.toLowerCase().includes(trackLower) ||
                    trackLower.includes(s.trackName.toLowerCase()),
                );
                if (matches.length === 1 && matches[0]) {
                  setSelectedSetupId(matches[0].id);
                }
              }
              // Reload the reference to create analysis record with the new storagePath
              if (selectedSetupId || customRefId) {
                setRefLoadTrigger((n) => n + 1);
              }
              return; // Done — fully loaded from cache
            }
          } catch {
            // Cache miss — fall through
          }
        }

        // Fallback: cached data not available (raw IBT was deleted)
        setError("Cached data not available for this file. Please re-upload.");
      })();
    },
    [loadCachedUser, setups],
  );

  /** Auto-load reference when setup is selected, or recreate analysis record on trigger */
  useEffect(() => {
    if (!selectedSetupId && !customRefId) return;
    if (selectedSetupId && loadReference.isPending) return;

    (async () => {
      setError(null);
      try {
        // Load setup reference data (skip if custom ref — data already in state)
        if (selectedSetupId) {
          setRefSource("setup");
          setCustomRefId(null);
          const result = await loadReference.mutateAsync({ setupId: selectedSetupId });
          if (!result.success) {
            setError(result.error);
            return;
          }
          setRefLapData(result.referenceLap);
          setTurnMarkers(result.turnMarkers);
          setRefWeather(result.weather);
        }

        // Create analysis record for AI coaching report
        if (storagePath && selectedFileName) {
          const analysisResult = await createAnalysis.mutateAsync({
            setupId: selectedSetupId || undefined,
            customReferencePath: customRefId || undefined,
            storagePath,
            fileName: selectedFileName,
            fileSize: file?.size ?? 0,
          });
          if (analysisResult.success) {
            setAnalysisId(analysisResult.analysisId);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unexpected error occurred");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when setupId/customRefId changes or refLoadTrigger bumps
  }, [selectedSetupId, customRefId, refLoadTrigger]);

  // Apply preselected setup from URL param — ref-guarded to prevent double-fire in StrictMode
  useEffect(() => {
    if (preselectedSetupId && !preselectedAppliedRef.current) {
      preselectedAppliedRef.current = true;
      setSelectedSetupId(preselectedSetupId);
      setRefLoadTrigger((n) => n + 1);
      // DO NOT call setSetupSearch — the dropdown label reads from
      // setups?.find(s => s.id === selectedSetupId), not from setupSearch.
    }
  }, [preselectedSetupId]);

  /** Upload a custom .ibt reference file */
  const handleCustomRefUpload = useCallback(
    (selectedFile: File | undefined) => {
      if (!selectedFile) return;
      if (!selectedFile.name.toLowerCase().endsWith(".ibt")) {
        setError("Only .ibt telemetry files are accepted");
        return;
      }
      if (selectedFile.size > 150 * 1024 * 1024) {
        setError("File exceeds maximum size of 150 MB");
        return;
      }
      // Only clear prior reference state once the file has passed validation.
      setRefLapData(null);
      setSelectedSetupId("");
      setRefSource("custom");
      setCustomRefId(null);
      setAnalysisId(null);
      setRefWeather(null);
      setError(null);

      (async () => {
        setStatus("Uploading reference...");
        try {
          const urlResult = await createUploadUrl.mutateAsync({
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
          });
          if (!urlResult.success) {
            setError(urlResult.error);
            setStatus("");
            return;
          }

          const uploadRes = await fetch(urlResult.signedUrl, {
            method: "PUT",
            headers: { "Content-Type": "application/octet-stream" },
            body: selectedFile,
          });
          if (!uploadRes.ok) {
            setError("Failed to upload reference file");
            setStatus("");
            return;
          }

          setStatus("Parsing reference...");
          const result = await uploadCustomRef.mutateAsync({
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            storagePath: urlResult.tempPath,
          });

          if (!result.success) {
            setError(result.error);
            setStatus("");
            return;
          }

          setRefLapData(result.referenceLap);
          setTurnMarkers(result.turnMarkers);
          setRefWeather(result.weather);
          setCustomRefId(result.referenceId);
          toast.success("Reference uploaded");

          // Create analysis record for AI report (with custom reference path)
          if (storagePath && selectedFileName) {
            const analysisResult = await createAnalysis.mutateAsync({
              customReferencePath: result.referenceId,
              storagePath,
              fileName: selectedFileName,
              fileSize: file?.size ?? 0,
            });
            if (analysisResult.success) {
              setAnalysisId(analysisResult.analysisId);
            }
          }

          setStatus("");
        } catch (err) {
          setError(err instanceof Error ? err.message : "An unexpected error occurred");
          setStatus("");
        }
      })();
    },
    [createUploadUrl, uploadCustomRef, createAnalysis, storagePath, selectedFileName, file],
  );

  /** Select a previously uploaded custom reference */
  const handleSelectCustomRef = useCallback(
    (ref: { id: string }) => {
      setShowSetupDropdown(false);
      setSelectedSetupId("");
      setRefSource("custom");
      setCustomRefId(ref.id);
      setRefLapData(null);
      setRefWeather(null);
      setAnalysisId(null);
      setError(null);

      (async () => {
        try {
          const result = await loadUserRef.mutateAsync({ referenceId: ref.id });
          if (!result.success) {
            setError(result.error);
            return;
          }
          setRefLapData(result.referenceLap);
          setTurnMarkers(result.turnMarkers);
          setRefWeather(result.weather);

          // Create analysis record for AI report
          if (storagePath && selectedFileName) {
            const analysisResult = await createAnalysis.mutateAsync({
              customReferencePath: ref.id,
              storagePath,
              fileName: selectedFileName,
              fileSize: file?.size ?? 0,
            });
            if (analysisResult.success) {
              setAnalysisId(analysisResult.analysisId);
            }
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : "An unexpected error occurred");
        }
      })();
    },
    [loadUserRef, createAnalysis, storagePath, selectedFileName, file],
  );

  /** Delete a custom reference (confirm first) */
  const handleDeleteCustomRef = useCallback(
    async (refId: string, label: string) => {
      const confirmed = window.confirm(
        `Delete custom reference "${label}"? This cannot be undone.`,
      );
      if (!confirmed) return;
      const result = await deleteUserRef.mutateAsync({ referenceId: refId });
      if (!result.success) {
        toast.error(result.error);
        return;
      }
      toast.success("Reference deleted");
      if (customRefId === refId) {
        setCustomRefId(null);
        setRefLapData(null);
        setRefWeather(null);
        setRefSource(null);
        setAnalysisId(null);
      }
    },
    [deleteUserRef, customRefId],
  );

  /** Warn if the selected reference doesn't match the user's detected track/car */
  const refMismatch = useMemo(() => {
    if (!detectedInfo) return null;
    const userTrack = detectedInfo.track.toLowerCase();
    const userCar = detectedInfo.car.toLowerCase();

    let refTrack = "";
    let refCar = "";
    if (selectedSetupId) {
      const s = setups?.find((s) => s.id === selectedSetupId);
      if (!s) return null;
      refTrack = s.trackName.toLowerCase();
      refCar = s.carName.toLowerCase();
    } else if (customRefId) {
      const r = userReferences?.find((r) => r.id === customRefId);
      if (!r) return null;
      refTrack = (r.trackName ?? "").toLowerCase();
      refCar = (r.carName ?? "").toLowerCase();
    } else {
      return null;
    }

    const trackMatch =
      !refTrack || !userTrack || refTrack.includes(userTrack) || userTrack.includes(refTrack);
    const carMatch = !refCar || !userCar || refCar.includes(userCar) || userCar.includes(refCar);
    if (trackMatch && carMatch) return null;
    return { trackMismatch: !trackMatch, carMismatch: !carMatch };
  }, [detectedInfo, selectedSetupId, customRefId, setups, userReferences]);

  const coachingInput = useMemo(() => {
    if (!userLapData || !refLapData) return null;
    return buildCoachingInput(
      userLapData,
      refLapData,
      turnMarkers,
      detectedInfo?.track ?? "Unknown Track",
      detectedInfo?.car ?? "Unknown Car",
    );
  }, [userLapData, refLapData, turnMarkers, detectedInfo]);

  // Premium gate AFTER all hooks
  // Show loading spinner while profile/subscription data is being fetched
  if (profileLoading || subLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (!subscription || subscription.tier !== "premium") {
    return <PremiumGate />;
  }

  const hasChart = !!userLapData || !!refLapData;

  return (
    <div className="mx-auto max-w-7xl px-6 py-6">
      <div className="mb-4">
        <h1 className="font-display text-3xl font-bold tracking-tight">Laptime Assistant</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Compare your telemetry against our pro drivers and get AI coaching tips.
        </p>
      </div>

      <div className="block md:hidden rounded-lg border border-border/40 bg-card p-8 text-center">
        <p className="text-muted-foreground">
          The Laptime Assistant requires a desktop browser. Please visit this page on your computer.
        </p>
      </div>

      <div className="hidden md:block space-y-4">
        {/* Control bar */}
        <div className="rounded-lg border border-border/40 bg-card p-3">
          <div className="flex items-start gap-3 flex-wrap">
            {/* File selector — dropdown with past files + upload new */}
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Your Telemetry
              </label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".ibt"
                  className="hidden"
                  onChange={(e) => {
                    handleFileSelect(e.target.files?.[0]);
                    setShowFileDropdown(false);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  disabled={isProcessing}
                />
                <div ref={fileDropdownRef} className="relative">
                  {/* Trigger button */}
                  <button
                    type="button"
                    className={`flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs transition-colors disabled:opacity-50 ${
                      selectedFileName
                        ? "border-input bg-background hover:bg-muted/30"
                        : "min-w-[220px] border-[#6B9FD4] bg-[#6B9FD4]/10 text-[#6B9FD4] hover:bg-[#6B9FD4]/20"
                    }`}
                    onClick={() => setShowFileDropdown((v) => !v)}
                    disabled={isProcessing}
                  >
                    <Upload className="h-3 w-3 shrink-0" />
                    <span className={selectedFileName ? "text-foreground" : "font-medium"}>
                      {selectedFileName || "Select telemetry file..."}
                    </span>
                    <ChevronDown className="h-3 w-3 shrink-0 ml-1" />
                  </button>

                  {/* Dropdown */}
                  {showFileDropdown && (
                    <div
                      className="absolute z-50 mt-1 min-w-[400px] w-max rounded-md border border-border/40 bg-card shadow-lg"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {/* Upload new button */}
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 border-b border-border/30 px-3 py-2 text-left text-xs text-primary transition-colors hover:bg-muted/50"
                        onClick={() => {
                          setShowFileDropdown(false);
                          fileInputRef.current?.click();
                        }}
                      >
                        <Upload className="h-3.5 w-3.5" />
                        Upload new .ibt file...
                      </button>

                      {/* Past files list */}
                      {userFiles && userFiles.length > 0 ? (
                        <div className="max-h-60 overflow-y-auto scrollbar-visible">
                          <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                            Recent uploads
                          </p>
                          {userFiles.map((uf) => (
                            <button
                              key={uf.storagePath}
                              type="button"
                              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-muted/50 ${uf.storagePath === storagePath ? "bg-muted/40 text-foreground" : "text-muted-foreground"}`}
                              onClick={() => handleSelectPastFile(uf)}
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-foreground">{uf.fileName}</p>
                                <p className="text-[10px] text-muted-foreground/60">
                                  {new Date(uf.uploadedAt).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="px-3 py-3 text-center text-xs text-muted-foreground">
                          No previous uploads
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Detected info — show here only before chart loads */}
              {detectedInfo && !status && !hasChart && (
                <div className="mt-1 flex items-center gap-1.5 rounded bg-muted/30 px-2 py-0.5">
                  <span className="text-[10px] text-muted-foreground">Detected:</span>
                  <span className="text-[10px] font-medium">
                    {detectedInfo.car} @ {detectedInfo.track}
                  </span>
                </div>
              )}
            </div>

            <div className="h-8 w-px bg-border/40 mt-5" />

            {/* Reference — searchable dropdown with setup refs + custom refs */}
            <div ref={setupDropdownRef} className="relative min-w-[250px]">
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Reference
              </label>
              {/* Hidden file input for custom reference upload */}
              <input
                ref={refFileInputRef}
                type="file"
                accept=".ibt"
                className="hidden"
                onChange={(e) => {
                  handleCustomRefUpload(e.target.files?.[0]);
                  setShowSetupDropdown(false);
                  if (refFileInputRef.current) refFileInputRef.current.value = "";
                }}
                disabled={isProcessing}
              />
              {/* Trigger button */}
              <button
                type="button"
                className="flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-2 text-xs transition-colors hover:bg-muted/30 disabled:opacity-50"
                onClick={() => setShowSetupDropdown((v) => !v)}
                disabled={isProcessing || setupsLoading}
              >
                <span
                  className={`truncate ${selectedSetupId || customRefId ? "text-foreground" : "text-muted-foreground"}`}
                >
                  {selectedSetupId
                    ? (() => {
                        const s = setups?.find((s) => s.id === selectedSetupId);
                        return s ? `${s.carName} @ ${s.trackName}` : "Select reference...";
                      })()
                    : customRefId
                      ? (() => {
                          const r = userReferences?.find((r) => r.id === customRefId);
                          return r
                            ? `${r.carName ?? "Unknown"} @ ${r.trackName ?? "Unknown"}`
                            : "Custom reference";
                        })()
                      : setupsLoading
                        ? "Loading..."
                        : "Select reference..."}
                </span>
                <div className="flex items-center gap-0.5 shrink-0">
                  {(selectedSetupId || customRefId) && (
                    <span
                      role="button"
                      tabIndex={0}
                      className="rounded p-0.5 hover:bg-muted/60"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSetupId("");
                        setCustomRefId(null);
                        setRefSource(null);
                        setSetupSearch("");
                        setRefLapData(null);
                        setAnalysisId(null);
                        setRefWeather(null);
                        setShowSetupDropdown(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.stopPropagation();
                          setSelectedSetupId("");
                          setCustomRefId(null);
                          setRefSource(null);
                          setSetupSearch("");
                          setRefLapData(null);
                          setAnalysisId(null);
                          setRefWeather(null);
                          setShowSetupDropdown(false);
                        }
                      }}
                    >
                      <X className="h-3 w-3 text-muted-foreground" />
                    </span>
                  )}
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </div>
              </button>
              {/* Dropdown with search + custom ref upload + sections */}
              {showSetupDropdown && (
                <div
                  className="absolute z-50 mt-1 w-full min-w-[350px] rounded-md border border-border/40 bg-card shadow-lg"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {/* Search input */}
                  <div className="relative border-b border-border/30 p-1.5">
                    <Search className="absolute left-3.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={setupSearch}
                      onChange={(e) => setSetupSearch(e.target.value)}
                      className="h-7 w-full rounded border-none bg-muted/30 pl-7 pr-2 text-xs placeholder:text-muted-foreground focus:outline-none"
                      autoFocus
                    />
                  </div>
                  {/* Upload custom reference button — also acts as drop zone */}
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 border-b border-border/30 px-3 py-2 text-left text-xs text-primary transition-colors hover:bg-muted/50"
                    onClick={() => {
                      setShowSetupDropdown(false);
                      refFileInputRef.current?.click();
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add("bg-primary/10");
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove("bg-primary/10");
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove("bg-primary/10");
                      const dropped = e.dataTransfer.files[0];
                      if (dropped) {
                        setShowSetupDropdown(false);
                        handleCustomRefUpload(dropped);
                      }
                    }}
                  >
                    <Upload className="h-3.5 w-3.5" />
                    Upload custom reference .ibt...{" "}
                    <span className="text-muted-foreground">(or drop here)</span>
                  </button>
                  <div className="max-h-60 overflow-y-auto scrollbar-visible">
                    {/* Your References section */}
                    {userReferences && userReferences.length > 0 && (
                      <>
                        <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                          Your References
                        </p>
                        {userReferences
                          .filter((r) => {
                            if (!setupSearch) return true;
                            const s = setupSearch.toLowerCase();
                            return (
                              (r.carName ?? "").toLowerCase().includes(s) ||
                              (r.trackName ?? "").toLowerCase().includes(s) ||
                              r.fileName.toLowerCase().includes(s)
                            );
                          })
                          .map((r) => (
                            <div
                              key={r.id}
                              className={`group flex w-full items-center px-3 py-1.5 text-xs transition-colors hover:bg-muted/50 ${r.id === customRefId ? "bg-muted/40 text-foreground" : ""}`}
                            >
                              <button
                                type="button"
                                className="flex flex-1 items-center justify-between text-left"
                                onClick={() => handleSelectCustomRef(r)}
                              >
                                <span>
                                  {r.carName ?? "Unknown"} @ {r.trackName ?? "Unknown"}
                                </span>
                                <span className="ml-2 shrink-0 text-[10px] text-muted-foreground/60">
                                  {new Date(r.createdAt).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })}
                                </span>
                              </button>
                              <button
                                type="button"
                                title="Delete reference"
                                className="ml-2 rounded p-1 text-muted-foreground/60 opacity-0 transition-opacity hover:bg-destructive/20 hover:text-destructive group-hover:opacity-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCustomRef(
                                    r.id,
                                    `${r.carName ?? "Unknown"} @ ${r.trackName ?? "Unknown"}`,
                                  );
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                      </>
                    )}
                    {/* Setup References section */}
                    <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                      Setup References
                    </p>
                    {filteredSetups.length === 0 ? (
                      <div className="px-3 py-3 text-center text-xs text-muted-foreground">
                        No setups found
                      </div>
                    ) : (
                      filteredSetups.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className={`flex w-full items-center px-3 py-1.5 text-left text-xs transition-colors hover:bg-muted/50 ${s.id === selectedSetupId ? "bg-muted/40 text-foreground" : ""}`}
                          onClick={() => {
                            setSelectedSetupId(s.id);
                            setCustomRefId(null);
                            setRefSource("setup");
                            setSetupSearch("");
                            setShowSetupDropdown(false);
                            setAnalysisId(null);
                            setRefLapData(null);
                            setRefLoadTrigger((n) => n + 1);
                          }}
                        >
                          {s.carName} @ {s.trackName} — {s.driverName}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Inline progress for reference upload */}
          {(uploadCustomRef.isPending || loadUserRef.isPending) && (
            <div className="mt-2 flex items-center gap-2">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-border/30">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#6B9FD4] to-[#3B9B7A] transition-all duration-300 ease-out"
                  style={{ width: `${fakeProgress}%` }}
                />
              </div>
              <span className="text-[10px] text-muted-foreground shrink-0">{status}</span>
            </div>
          )}

          {refMismatch && (
            <div className="mt-2 flex items-center gap-1.5 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-400">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              <span>
                Reference{" "}
                {refMismatch.trackMismatch && refMismatch.carMismatch
                  ? "track and car don't"
                  : refMismatch.trackMismatch
                    ? "track doesn't"
                    : "car doesn't"}{" "}
                match your telemetry — comparison may be misleading.
              </span>
            </div>
          )}

          {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
        </div>

        {/* Session summary table — includes track map on the right */}
        {hasChart &&
          (() => {
            const selected = setups?.find((s) => s.id === selectedSetupId);
            const t = detectedInfo?.track?.toLowerCase();
            const matchByDetected =
              t && setups
                ? setups.find(
                    (s) =>
                      s.trackName.toLowerCase().includes(t) ||
                      t.includes(s.trackName.toLowerCase()),
                  )
                : null;
            const svgUrl = selected?.trackMapSvgUrl ?? matchByDetected?.trackMapSvgUrl ?? null;
            const sfUrl =
              selected?.trackMapStartFinishUrl ?? matchByDetected?.trackMapStartFinishUrl ?? null;
            const mapNode = svgUrl ? (
              <TrackMapCursor mapUrl={svgUrl} distPct={cursorDistPct} startFinishUrl={sfUrl} />
            ) : null;
            return (
              <SessionSummary
                userLap={userLapData}
                referenceLap={refLapData}
                userWeather={userWeather}
                refWeather={refWeather}
                detectedInfo={detectedInfo}
                mapSlot={mapNode}
              />
            );
          })()}

        {/* Chat-style AI coach popup */}
        {hasChart && userLapData && refLapData && (
          <AICoachPopup analysisId={analysisId} coachingInput={coachingInput} />
        )}

        {/* Chart area */}
        {hasChart ? (
          <ChartContainer
            userLap={userLapData}
            referenceLap={refLapData}
            turnMarkers={turnMarkers}
            trackLengthKm={setups?.find((s) => s.id === selectedSetupId)?.trackLengthKm ?? 4.0}
            userLapTime={userLapData?.lap_time}
            refLapTime={refLapData?.lap_time}
            onCursorChange={setCursorDistPct}
          />
        ) : (
          <div
            className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed bg-card py-24 text-center min-h-[500px] transition-colors ${
              dragOver
                ? "border-primary/60 bg-primary/5"
                : "border-border/40 hover:border-border/60"
            } ${!showProgress && !isProcessing ? "cursor-pointer" : ""}`}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const droppedFile = e.dataTransfer.files[0];
              if (droppedFile) handleFileSelect(droppedFile);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragOver(false);
            }}
            onClick={() => {
              if (!showProgress && !isProcessing) fileInputRef.current?.click();
            }}
          >
            {showProgress ? (
              <div className="w-full max-w-md px-8 animate-in fade-in duration-300">
                {/* Progress bar */}
                <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-border/30">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#6B9FD4] to-[#3B9B7A] transition-all duration-300 ease-out"
                    style={{ width: `${fakeProgress}%` }}
                  />
                </div>
                <p
                  className="text-sm text-muted-foreground transition-opacity duration-500"
                  key={funMessage}
                >
                  {funMessage}
                </p>
              </div>
            ) : (
              <div className="animate-in fade-in duration-500">
                <Upload className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">
                  Drop your .ibt file here or click to browse
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
